import * as React from 'react';

// @ts-expect-error
import decapitalize from 'underscore.string/decapitalize';

import type {FilterObject} from 'nutshell-core/types';

import {Card} from 'shells/card/card';
import {useNutshellTour} from 'shells/tours/use-nutshell-tour';

import {SegmentedTimeseriesChartContainer} from './charts/segmented-timeseries-chart-container';
import {ReportChartHeader} from './report-chart-header';
import {
    GAP_SELECT_VALUES_DAY_TO_YEAR,
    getReportTitle,
    CHART_HERO_DIV_ID,
    getReportTitleIsAlwaysShown,
} from './report-constants';
import type {ChartReportParams, ReportKey, ReportGap} from './report-types';

type Props = {
    activeFilters: FilterObject[];
    onChartClick?: (filters: FilterObject) => void | null | undefined;
    onParamChange: (arg1: any, arg2: boolean) => void;
    reportParams: ChartReportParams;
    reportKey: ReportKey;
    xAxisLabel?: React.ReactNode;
    reportDataSummary?: React.ReactNode;
    hasBorder?: boolean;
    hasGap?: boolean;
    headerExtraContent?: React.ReactNode;
    height?: number;
    containerHeight?: number;
    isFullWidth?: boolean;
    fitHeight?: boolean;
    useBuiltInEmptyState?: boolean;
};

/*
 * Vertical bar chart shown in a card UI.  Initially designed for lead reports.
 *
 * If reportDataSummary prop is provided, we'll render it at the bottom of the chart.
 */
export function ReportChart(props: Props) {
    const {
        height = 400,
        containerHeight = 500,
        isFullWidth,
        hasGap = true,
        activeFilters,
        onChartClick,
        reportKey,
        reportParams,
        reportDataSummary,
        xAxisLabel,
        fitHeight,
        useBuiltInEmptyState = false,
    } = props;

    const {progressTour} = useNutshellTour();

    const handleGapChange = (gap: ReportGap) => {
        props.onParamChange({report: {gap}}, true);
    };

    const reportTitle = getReportTitle(reportKey);
    const isReportTitleAlwaysShown = getReportTitleIsAlwaysShown(reportKey);
    const gapLabel = getGapLabel(reportParams.gap);

    React.useEffect(() => {
        progressTour();
    }, [progressTour]);

    return (
        <div
            id={CHART_HERO_DIV_ID}
            style={{
                height: fitHeight ? 'fit-content' : containerHeight,
                minHeight: fitHeight ? undefined : containerHeight,
                width: isFullWidth ? '100%' : undefined,
            }}
        >
            <Card hasBorder={Boolean(props.hasBorder)} hasShadow={Boolean(props.hasBorder)}>
                <div className='full-width pos-relative background-white full-height'>
                    <ReportChartHeader
                        reportKey={reportKey}
                        gap={
                            hasGap
                                ? {
                                      reportGap: reportParams.gap,
                                      onUpdateGap: handleGapChange,
                                  }
                                : undefined
                        }
                        titleProps={{
                            title: gapLabel
                                ? `${reportTitle} by ${decapitalize(gapLabel)}`
                                : reportTitle,
                            isAlwaysShown: isReportTitleAlwaysShown,
                        }}
                        extraContent={props.headerExtraContent}
                    />
                    <SegmentedTimeseriesChartContainer
                        reportKey={reportKey}
                        dateRangeType={reportParams.dateRangeType}
                        height={height}
                        // @ts-expect-error
                        xAxisLabel={xAxisLabel}
                        filters={activeFilters}
                        facet={reportParams.facet}
                        onClickChartSegment={onChartClick}
                        useConfidence={reportParams.useConfidence}
                        segment={reportParams.segment}
                        compareTo={reportParams.compareTo}
                        reportGap={reportParams.gap}
                        useBuiltInEmptyState={useBuiltInEmptyState}
                    />
                    {reportDataSummary ? (
                        <div className='full-width mt-32'>{reportDataSummary}</div>
                    ) : null}
                </div>
            </Card>
        </div>
    );
}

function getGapLabel(gapId: ReportGap) {
    const foundGap = GAP_SELECT_VALUES_DAY_TO_YEAR.find((gap) => gap.id === gapId);

    if (foundGap) {
        return foundGap.label;
    }
}
